import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Language, LocationOn, Business } from '@mui/icons-material';
import axios from 'axios';
import {
  TextField,
  Button,
  CircularProgress,
  Grid,
  Typography,
  Box,
  Paper,
  Skeleton,
  Snackbar,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from '@mui/material';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';
import { makeStyles } from '@mui/styles';

const API_URL = process.env.REACT_APP_API_URL;

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(4),
    marginTop: theme.spacing(4),
    maxWidth: '1200px',
    margin: '0 auto',
  },
  formPaper: {
    padding: theme.spacing(4),
    borderRadius: '8px',
    boxShadow: theme.shadows[3],
    backgroundColor: theme.palette.background.paper,
    marginTop: theme.spacing(3),
  },
  inputField: {
    marginBottom: theme.spacing(2),
  },
  logoPreview: {
    width: '100px',
    height: '100px',
    objectFit: 'cover',
    borderRadius: '8px',
    marginBottom: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(2),
  },
  organizationDetails: {
    marginTop: theme.spacing(4),
    textAlign: 'center',
  },
  organizationImg: {
    borderRadius: '8px',
    marginBottom: theme.spacing(2),
    boxShadow: theme.shadows[3],
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  errorText: {
    marginTop: theme.spacing(2),
    color: theme.palette.error.main,
    textAlign: 'center',
  },
}));

const OrganizationPage = ({ userId, onLogout }) => {

  useEffect(() => {
    window.scrollTo(0, 0);  // Scroll to top when this page is loaded
  }, []);


  const classes = useStyles();
  const [organization, setOrganization] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    logo: null,
    industry_type: '',
    about: '',
    website: '',
    location_google_maps_link: '',
  });
  const [industryTypes, setIndustryTypes] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);


  const [isVerified, setIsVerified] = useState(false);
  // to track check status



  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const res = await axios.post(`${API_URL}/api/check-admin/`, { user_id: userId });
        setIsAdmin(res.data.is_admin);
      } catch (error) {
        console.error('Error checking admin status', error);
      }
    };

    if (userId) checkAdmin();
  }, [userId]);


  useEffect(() => {
    const checkVerification = async () => {
      if (!userId) return;

      try {
        const response = await axios.post(`${API_URL}/api/check-verification/`, {
          user_id: userId,
        });

        setIsVerified(response.data.status === 'approved');
      } catch (error) {
        console.error('Verification check failed:', error);
        setIsVerified(false);
      } finally {
        setLoading(false);
      }
    };

    checkVerification();
  }, [userId]);


  useEffect(() => {
    if (!userId) {
      navigate('/login');
    }
  }, [userId, navigate]);


  useEffect(() => {
    // Fetch available industry types
    axios
      .get(`${API_URL}/industry-types/`)
      .then((response) => {
        setIndustryTypes(response.data);
      })
      .catch((err) => {
        setError('Failed to fetch industry types. Please try again.');
      });

    // Fetch organization data
    axios
      .post(`${API_URL}/organization/check/`, { user_id: userId })
      .then((response) => {
        setOrganization(response.data.organization);
        setStatus(response.data.status);
      })
      .catch((err) => {
        if (err.response && err.response.status === 404) {
          setStatus('not_found');
        } else {
          setError('An error occurred while fetching the organization data');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [userId]);

  const handleChange = (event) => {
    const { name, value, type, files } = event.target;
    if (type === 'file') {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };
  // const handleLogout = () => {
  //   onLogout();
  //   navigate('/login');
  // };

  const handleSubmit = (event) => {
    event.preventDefault();

    const formDataToSend = new FormData();
    // Add all fields from formData to formDataToSend
    for (const key in formData) {
      formDataToSend.append(key, formData[key]);
    }

    // Include user_id in the request
    formDataToSend.append('user_id', userId);

    axios
      .post(`${API_URL}/organization/create/`, formDataToSend)
      .then((response) => {
        setStatus('pending');
        setSnackbarMessage('Organization created successfully!');
        setOpenSnackbar(true);
      })
      .catch((error) => {
        console.error('Error submitting form:', error);
        setError('Failed to create organization. Please try again.');
        setOpenSnackbar(true);
      });
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };
  const handleRedirect1 = () => {
    navigate('/show-organization-users');  // Redirect to meeting guidelines page
  };

  const handleRedirect2 = () => {
    navigate('/add-organization-users');  // Redirect to meeting guidelines page
  };

  if (loading) {
    return (
      <div className={classes.container}>
        <Header1 userId={userId} onLogout={onLogout} />
        <Skeleton variant="rectangular" width="100%" height={200} />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="90%" />
        <Footer2 />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Header1 userId={userId} onLogout={onLogout} />

      <Typography
        variant="h3"
        component="h1"
        align="center"
        fontSize={{ xs: '2rem', sm: '2.5rem', md: '3rem' }}
        sx={{
          backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
          mt: 10,
          letterSpacing: 10,
          WebkitBackgroundClip: 'text',  // This makes the gradient clip to the text
          color: 'transparent',  // Makes the text itself transparent to show the gradient
          // fontWeight: 'bold',  // Optional: you can make the text bold
        }}
        gutterBottom
      >
        Organization Page
      </Typography>


      {status === 'not_found' ? (
        <Box sx={{ minHeight: '50vh' }}>
          <Typography variant="h6" align="center" gutterBottom sx={{ letterSpacing: 5, }}>
            No organization found for this user. Please create one.
          </Typography>

          <Paper className={classes.formPaper}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Organization Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    fullWidth
                    required
                    className={classes.inputField}
                    sx={{ letterSpacing: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <input
                    type="file"
                    name="logo"
                    onChange={handleChange}
                    required
                    style={{ marginBottom: '16px', letterSpacing: 2 }}
                  />
                  <Typography style={{ color: '#6c757d', marginBottom: '8px', letterSpacing: 2 }}>
                    Upload your organization logo here.
                  </Typography>

                  {formData.logo && (
                    <img
                      src={URL.createObjectURL(formData.logo)}
                      alt="Logo Preview"
                      className={classes.logoPreview}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required className={classes.inputField}>
                    <InputLabel sx={{ letterSpacing: 2 }}>Industry Type</InputLabel>
                    <Select
                      label="Industry Type"
                      name="industry_type"
                      value={formData.industry_type}
                      onChange={handleChange}
                      sx={{ letterSpacing: 2 }}
                    >
                      {industryTypes.map((industry) => (
                        <MenuItem key={industry.id} value={industry.name} sx={{ letterSpacing: 2 }}>
                          {industry.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Website"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    fullWidth
                    required
                    helperText="Website link should start with https://"
                    sx={{ letterSpacing: 2 }}
                    className={classes.inputField}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="About the Organization"
                    name="about"
                    sx={{ letterSpacing: 2 }}
                    value={formData.about}
                    onChange={handleChange}
                    fullWidth
                    required
                    multiline
                    rows={4}
                    helperText={`${formData.about.length}/500 characters`}
                    FormHelperTextProps={{
                      style: {
                        letterSpacing: 2,
                        color: formData.about.length > 450 ? 'orange' : 'inherit', // Change color if over 450
                      },
                    }}
                    inputProps={{ maxLength: 500 }}
                    className={classes.inputField}
                  />

                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Google Maps Link"
                    name="location_google_maps_link"
                    value={formData.location_google_maps_link}
                    onChange={handleChange}
                    fullWidth
                    sx={{ letterSpacing: 2 }}
                    required
                    className={classes.inputField}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button type="submit" variant="contained" color="primary" disabled={!isVerified || loading} fullWidth className={classes.button} sx={{ letterSpacing: 2, backgroundColor: isVerified ? '#1976d2' : '#aaa', }}>
                    Submit Organization
                  </Button>
                  {!loading && !isVerified && (
                    <Typography
                      variant="body2"
                      color="#1E90FF"
                      sx={{ mt: 2, letterSpacing: 4, textAlign: 'center' }}
                    >
                      Only verified users are allowed to create organization
                    </Typography>
                  )}
                </Grid>

              </Grid>
            </form>

            {error && (
              <Typography color="error" variant="body2" mt={2} align="center" className={classes.errorText} sx={{ letterSpacing: 2 }}>
                {error}
              </Typography>
            )}
          </Paper>
        </Box>
      ) : status === 'pending' ? (
        <Box
          sx={{
            minHeight: '50vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            bgcolor: '#2222', // light gray background
            borderRadius: 2,
            boxShadow: 3,
            p: 4,
          }}
        >
          <Typography
            variant="h5"
            color="primary"
            align="center"
            sx={{ letterSpacing: 1, mb: 1 }}
          >
            Pending Approval
          </Typography>
          <Typography
            variant="body1"
            align="center"
            sx={{ letterSpacing: 0.5, color: 'text.secondary', maxWidth: 500 }}
          >
            Your organization is currently pending approval. You’ll be notified once the review process is complete.
          </Typography>
        </Box>

      ) : status === 'approved' && organization ? (
        <Grid container justifyContent="center">
          <Box sx={{
            padding: { xs: "2rem", sm: "3rem" }, // Responsive padding
            backgroundColor: "#2222", // Darker background for modern look
            color: "#fff",
            borderRadius: "16px",
            height: "100%",
            boxSizing: "border-box",
            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
            "&:hover": {
              transform: "translateY(-5px)",
              boxShadow: "0 8px 20px rgba(0, 0, 0, 0.3)",
            },
          }}>

            <Box className={classes.organizationDetails}>

              <Typography variant="h4" gutterBottom sx={{ letterSpacing: 2 }}>
                {organization.name}
              </Typography>

              <img
                src={`${API_URL}${organization.logo}`}
                alt={`${organization.name} logo`}
                style={{
                  maxWidth: '150px',
                  margin: '20px auto',  // Center image horizontally
                  borderRadius: '50%'    // Optional: round logo if you prefer
                }}
              />




              {/* Main Content */}
              <Paper sx={{
                padding: 3,
                backgroundColor: '#2222',  // Darker card background
                borderRadius: 2,
                boxShadow: 6,
                marginBottom: 3,
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  boxShadow: 10, // Increased shadow on hover for more effect
                  transform: 'scale(1.02)', // Slight zoom effect on hover
                },
              }}>
                <Typography
                  variant="h6"
                  paragraph
                  sx={{

                    lineHeight: 1.7,
                    color: '#e0e0e0',
                    fontWeight: 400,
                    letterSpacing: 2,
                    fontSize: {
                      xs: '1rem',  // Font size for extra small devices (mobile)
                      sm: '1.2rem', // Font size for small devices (tablet)
                      md: '1.4rem', // Font size for medium devices (small laptops)
                      lg: '1.4rem', // Font size for large devices (desktops)
                      xl: '1.2rem', // Font size for extra large devices (large desktops)
                    },
                    padding: {
                      xs: '10px',  // Padding for extra small screens
                      sm: '20px',  // Padding for small screens
                      md: '20px',  // Padding for medium screens
                      lg: '15px',  // Padding for large screens
                    },
                    marginBottom: {
                      xs: '20px',  // Bottom margin for small screens
                      sm: '30px',  // Bottom margin for medium screens
                      md: '15px',  // Bottom margin for larger screens
                    },
                  }}
                >
                  {organization.about}
                </Typography>

              </Paper>


              <Box
                sx={{
                  maxWidth: '1200px',
                  margin: 'auto',
                  padding: 3,
                  marginTop: 4,
                  backgroundColor: '#121212',
                  color: '#e0e0e0',
                  borderRadius: 2,
                  boxShadow: 3,
                }}
              >
                <Grid container spacing={3}>
                  {/* Reusable Paper Box Style */}
                  {[
                    {
                      icon: <Business sx={{ fontSize: 40, color: '#1976d2', marginRight: 2 }} />,
                      label: 'Industry:',
                      content: organization.industry_type,
                    },
                    {
                      icon: <Language sx={{ fontSize: 40, color: '#1976d2', marginRight: 2 }} />,
                      label: 'Website:',
                      content: (
                        <Box
                          onClick={() => window.open(organization.website, '_blank')}
                          sx={{
                            color: '#007BFF',
                            cursor: 'pointer',
                            textDecoration: 'none',
                            letterSpacing: 2,
                            fontWeight: 600,
                            transition: 'color 0.3s ease',
                            '&:hover': {
                              color: '#0056b3',
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          Click to Visit
                        </Box>
                      ),
                    },
                    {
                      icon: <LocationOn sx={{ fontSize: 40, color: '#1976d2', marginRight: 2 }} />,
                      label: 'Location:',
                      content: (
                        <Box
                          onClick={() => window.open(organization.location_google_maps_link, '_blank')}
                          sx={{
                            color: '#1E90FF',
                            cursor: 'pointer',
                            textDecoration: 'none',
                            letterSpacing: 2,
                            fontWeight: 600,
                            transition: 'color 0.3s ease',
                            '&:hover': {
                              color: '#0d6efd',
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          Click to View on Map
                        </Box>
                      ),
                    },
                  ].map((item, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex' }}>
                      <Paper
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          padding: 2,
                          backgroundColor: '#333',
                          borderRadius: 2,
                          boxShadow: 2,
                          transition: 'all 0.3s ease-in-out',
                          height: '100%',
                          width: '100%',
                          '&:hover': {
                            boxShadow: 6,
                            transform: 'scale(1.05)',
                          },
                        }}
                      >
                        {item.icon}
                        <Box>
                          <Typography
                            variant="body1"
                            sx={{ color: '#e0e0e0', fontWeight: 500, letterSpacing: 2 }}
                          >
                            <strong>{item.label}</strong> {item.content}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>


            </Box>
            <Box sx={{ textAlign: 'center', padding: { xs: 2, sm: 3 } }}>
              <Grid container spacing={2} justifyContent="center">
                <Grid item xs={12} sm="auto">
                  <Button
                    variant="contained"
                    color="info"
                    onClick={handleRedirect1}
                    sx={{
                      width: { xs: '100%', sm: 'auto' },  // Full width on small screens, auto on larger screens
                      padding: { xs: '0.75rem 1rem', sm: '0.875rem 1.5rem' },  // Adjust padding for different screen sizes
                      fontSize: { xs: '0.875rem', sm: '1rem' },  // Adjust font size for responsiveness
                      textAlign: 'center',  // Ensures text is centered
                      marginTop: { xs: '1rem', sm: '1.5rem' },  // Adjust margin top for spacing
                      letterSpacing: 2
                    }}
                  >
                    Show Users
                  </Button>
                </Grid>

                {isAdmin && (
                  <Grid item xs={12} sm="auto">
                    <Button
                      variant="contained"
                      color="info"
                      onClick={handleRedirect2}
                      sx={{
                        width: { xs: '100%', sm: 'auto' },
                        padding: { xs: '0.75rem 1rem', sm: '0.875rem 1.5rem' },
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        textAlign: 'center',
                        marginTop: { xs: '1rem', sm: '1.5rem' },
                        letterSpacing: 2,
                      }}
                    >
                      Manage Users
                    </Button>
                  </Grid>
                )}

              </Grid>
            </Box>
          </Box>
        </Grid>
      ) : null}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
      />

      <Footer2 />
    </div>
  );
};

export default OrganizationPage;
